import type { ManualChapter } from './types'

export const eventsChapter: ManualChapter = {
  id: 'events',
  number: 7,
  title: 'Eventi',
  description: 'Calendario, prenotazioni e webinar',
  icon: 'CalendarIcon',
  sections: [
    {
      id: 'calendar',
      title: 'Calendario Eventi',
      requirement: '§2.9 Gestione eventi online/offline',
      content: `
## Calendario Eventi

La sezione eventi ti permette di scoprire e partecipare a webinar, workshop e conferenze.

![Calendario Eventi](events-calendar.png)
*Il calendario degli eventi*

### Viste Disponibili

| Vista | Descrizione |
|-------|-------------|
| **Calendario** | Vista mensile con eventi |
| **Lista** | Elenco cronologico |

### Navigazione

1. Clicca su **"Eventi"** nel menu
2. Scegli la vista preferita
3. Naviga tra i mesi con le frecce
4. Clicca su un evento per i dettagli
`,
      screenshots: [
        {
          id: 'events-calendar',
          path: '/assets/manual/events-calendar.png',
          caption: 'Calendario eventi',
          alt: 'Vista calendario con eventi'
        }
      ]
    },
    {
      id: 'event-types',
      title: 'Tipi di Evento',
      content: `
## Tipi di Evento

La piattaforma supporta diversi tipi di eventi:

| Tipo | Descrizione | Icona |
|------|-------------|-------|
| **Webinar** | Presentazione online | 🎥 |
| **Workshop** | Sessione pratica interattiva | 🛠️ |
| **Conferenza** | Evento con più speaker | 🎤 |
| **Training** | Sessione formativa | 📚 |
| **Meeting** | Riunione di gruppo | 👥 |
| **Community Day** | Evento annuale della community | 🎉 |

### Modalità

| Modalità | Descrizione |
|----------|-------------|
| **Online** | Via Jitsi Meet dalla piattaforma |
| **In presenza** | Presso sede fisica |
| **Ibrido** | Entrambe le modalità |
`
    },
    {
      id: 'event-detail',
      title: 'Dettaglio Evento',
      content: `
## Dettaglio Evento

![Dettaglio Evento](event-detail.png)
*Pagina di dettaglio di un evento*

### Informazioni Visualizzate

| Sezione | Contenuto |
|---------|-----------|
| **Titolo** | Nome dell'evento |
| **Data e ora** | Quando si terrà |
| **Durata** | Quanto dura |
| **Tipo** | Webinar, Workshop, etc. |
| **Modalità** | Online/In presenza |
| **Location** | Dove si terrà (se fisico) |
| **Speaker** | Chi presenta |
| **Descrizione** | Di cosa tratta |
| **Agenda** | Programma dettagliato |
| **Posti** | Disponibilità |

### Agenda dell'Evento

L'agenda mostra:
- **Orari** delle sessioni
- **Titolo** di ogni intervento
- **Speaker** che presenta
- **Descrizione** breve
`,
      screenshots: [
        {
          id: 'event-detail',
          path: '/assets/manual/event-detail.png',
          caption: 'Dettaglio evento',
          alt: 'Pagina con informazioni evento'
        }
      ]
    },
    {
      id: 'booking',
      title: 'Prenotazione',
      requirement: '§2.9 Prenotazioni utenti e liste d\'attesa',
      content: `
## Prenotazione Eventi

### Come Prenotarsi

1. Apri il **dettaglio dell'evento**
2. Verifica **data, ora e disponibilità**
3. Clicca su **"Iscriviti"** o **"Prenota"**
4. Conferma la prenotazione

### Stati Prenotazione

| Stato | Descrizione |
|-------|-------------|
| **Iscritto** | Prenotazione confermata |
| **Lista d'attesa** | Posti esauriti, in coda |
| **Partecipato** | Hai partecipato all'evento |
| **Non presentato** | Non ti sei presentato |
| **Cancellato** | Hai disdetto |

### Lista d'Attesa

Se l'evento è pieno:
1. Puoi iscriverti alla **lista d'attesa**
2. Se si libera un posto, verrai **avvisato**
3. La priorità è in ordine di iscrizione

### Cancellare la Prenotazione

1. Vai su **I miei eventi** o **Dettaglio evento**
2. Clicca su **"Cancella iscrizione"**
3. Conferma la cancellazione

> **Nota**: Cancella con anticipo per permettere ad altri di partecipare.
`
    },
    {
      id: 'online-participation',
      title: 'Partecipare Online',
      requirement: '§2.9 Integrazione con Zoom/Teams → Jitsi Meet',
      content: `
## Partecipare agli Eventi Online

Gli eventi online si svolgono tramite **Jitsi Meet**, una piattaforma di videoconferenza open-source.

### Come Partecipare

1. **Prima dell'evento**: Ricevi promemoria via email
2. **All'ora dell'evento**: Vai al dettaglio evento
3. **Clicca su "Partecipa"**: Si apre Jitsi Meet
4. **Consenti** accesso a webcam e microfono (opzionale)
5. **Entra** nella stanza

### Funzionalità Jitsi Meet

| Funzione | Descrizione |
|----------|-------------|
| **Video** | Attiva/disattiva webcam |
| **Audio** | Attiva/disattiva microfono |
| **Chat** | Messaggi testuali |
| **Condivisione schermo** | Mostra il tuo schermo |
| **Alzata di mano** | Segnala che vuoi parlare |
| **Reazioni** | Emoji rapide |

### Requisiti Tecnici

- **Browser** aggiornato (Chrome consigliato)
- **Connessione** stabile (min 3 Mbps)
- **Webcam** e **microfono** (opzionali)

### Suggerimenti

- Entra **5 minuti prima** dell'inizio
- Testa **audio e video** prima di iniziare
- Silenzia il microfono quando non parli
- Usa le **cuffie** per evitare eco
`
    },
    {
      id: 'materials',
      title: 'Materiali Post-Evento',
      requirement: '§2.9 Materiali post-evento (slide, video)',
      content: `
## Materiali Post-Evento

Dopo ogni evento vengono pubblicati i materiali.

### Tipi di Materiali

| Tipo | Descrizione |
|------|-------------|
| **Slide** | Presentazioni degli speaker |
| **Video** | Registrazione dell'evento |
| **Documenti** | Materiali di approfondimento |
| **Esercitazioni** | Attività pratiche |

### Come Accedere

1. Vai al **dettaglio dell'evento** (anche passato)
2. Scorri alla sezione **"Materiali"**
3. Clicca per **visualizzare** o **scaricare**

### Notifiche

Riceverai una **notifica** quando:
- I materiali sono disponibili
- Viene aggiunta la registrazione video

> **Nota**: I materiali sono disponibili solo per chi era iscritto all'evento.
`
    },
    {
      id: 'community-day',
      title: 'Community Day',
      requirement: '§2.9 Modulo dedicato al Community Day con votazioni e programma',
      content: `
## Community Day

Il Community Day è l'**evento annuale principale** della community.

### Caratteristiche Speciali

- **Proposta talk**: Puoi proporre un intervento
- **Votazioni**: Vota le proposte degli altri
- **Programma collaborativo**: Costruito con la community

### Proporre un Talk

1. Vai alla sezione **Community Day**
2. Clicca su **"Proponi un talk"**
3. Compila:
   - **Titolo** dell'intervento
   - **Abstract** (descrizione)
   - **Durata** proposta
   - **Categoria** tematica
4. Invia la proposta

### Votare le Proposte

1. Sfoglia le **proposte** degli altri
2. Clicca **"Vota"** per quelle che ti interessano
3. Le più votate entreranno nel programma

### Programma Finale

- Le proposte più votate vengono selezionate
- Il programma finale viene pubblicato
- Ricevi notifica con l'agenda completa
`
    }
  ]
}
